import React from 'react';
import { FaTrash, FaPlus, FaEdit, FaGripVertical } from 'react-icons/fa';
import { Language_Text_Editor } from './language_text_editor.js';
import {
  DragDropContext,
  Droppable,
  Draggable
} from 'react-beautiful-dnd';
import { Colors } from './form_builder.js';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";

/**
 * Answers_Builder component
 * @param {Array} answers - Array of answer objects with answerText[]
 * @param {string} language - Current language
 * @param {function} onTextChange - (index, updatedTextArray)
 * @param {function} onDelete - (index)
 * @param {function} onAdd - ()
 * @param {function} onReorder - (reorderedAnswers)
 * @param {function} onEdit - (index) optional
 * @param {string} parentQuestionCode - Code of the parent question for hierarchical codes
 */
const Answers_Builder = ({ 
  answers, 
  language, 
  onTextChange, 
  onDelete, 
  onAdd, 
  onReorder, 
  onEdit,
  colors = Colors,
  isEditing = false,
  parentEditContext = null,
  isBooleanType = false,
  parentQuestionCode = null
}) => {
  const { t } = useTranslation();

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reordered = [...answers];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    onReorder(reordered);
  };

  // Labels are now managed centrally in menu.js via form_builder_labels.js
  // No need to set up individual labels here as they are loaded automatically

  // Select appropriate contextual colors based on parent context
  let editContextColors = colors.editContextAnswer;
  if (parentEditContext === 'question') {
    editContextColors = colors.editContextQuestion;
  } else if (parentEditContext === 'section') {
    editContextColors = colors.editContextSection;
  }

  // Determine visual style based on context
  const getBorderStyle = () => {
    if (!isEditing) return {};
    return { 
      borderColor: editContextColors.border, 
      borderWidth: parentEditContext ? '1px' : '2px' 
    };
  };

  const getBodyStyle = () => {
    if (!isEditing) return {};
    return { backgroundColor: editContextColors.body };
  };

  return (
    <div className="card shadow-sm w-100" style={getBorderStyle()}>
      <div className="card-header d-flex justify-content-between align-items-center"
        style={{ backgroundColor: colors.secondaryLight }}
      >
        <h6 className="mb-0">
          {isEditing && !parentEditContext && 
            <span className="badge me-2" style={{ backgroundColor: editContextColors.badge, color: colors.white }}>
              {t('editing', { ns: 'labels' })}
            </span>
          }
          {isBooleanType ? t('trueFalseOptions', { ns: 'labels' }) : t('answerOptions', { ns: 'labels' })}
        </h6>
        <button 
          className="btn btn-sm" 
          style={{ 
            backgroundColor: colors.white, 
            color: colors.dark 
          }}
          onClick={onAdd}
          title={t('addAnswer', { ns: 'labels' })}
        >
          <FaPlus className="me-1" />
          {t('addAnswer', { ns: 'labels' })}
        </button>
      </div>

      <div className="card-body p-3" style={getBodyStyle()}>
        {answers.length === 0 ? (
          <div className="text-center py-4 text-muted w-100">
            <p>{t('noAnswers', { ns: 'labels' })}</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="answers">
              {(provided, snapshot) => (
                <div 
                  ref={provided.innerRef} 
                  {...provided.droppableProps}
                  className="answers-list w-100"
                  style={{
                    transition: 'background-color 0.2s ease',
                    backgroundColor: snapshot.isDraggingOver ? colors.primaryLight : 'transparent'
                  }}
                >
                  {answers.map((answer, index) => (
                    <Draggable 
                      key={`answer-${answer.id || index}`} 
                      draggableId={`answer-${answer.id || index}`} 
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          className={`card mb-2 w-100 ${snapshot.isDragging ? 'border-primary' : ''}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            transition: snapshot.isDragging ? 'transform 0.2s ease, box-shadow 0.2s ease' : 'none',
                            ...(snapshot.isDragging ? { 
                              borderColor: colors.primary,
                              cursor: 'grabbing',
                              boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                              zIndex: 1000
                            } : {
                              transform: 'none',
                              transition: 'none'
                            }),
                            // Apply reduced opacity for invisible answers
                            opacity: answer.invisible ? 0.6 : 1,
                          }}
                        >
                          <div className="card-body py-2 px-3">
                            <div className="row align-items-center w-100 g-0">
                              <div className="col-auto" {...provided.dragHandleProps}>
                                <FaGripVertical className="text-muted" />
                              </div>
                              <div className="col">
                                <Language_Text_Editor
                                  language={language}
                                  mode="textarea"
                                  text={answer.answerText}
                                  onChange={(updated) => onTextChange(index, updated)}
                                  colors={colors}
                                />
                                <div className="mt-1 text-muted small d-flex">
                                  {answer.score !== null && answer.score !== undefined && (
                                    <span className="me-3">{t('score', { ns: 'labels' })}: <strong>{answer.score}</strong></span>
                                  )}
                                  {answer.answerCode !== null && answer.answerCode !== undefined && answer.answerCode !== '' && (
                                    <span>{t('code', { ns: 'labels' })}: <strong>{answer.answerCode}</strong></span>
                                  )}
                                  {answer.invisible !== undefined && (
                                    <span className="ms-3">{t('invisible', { ns: 'labels' })}: <strong>{answer.invisible ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}</strong></span>
                                  )}
                                </div>
                              </div>
                              <div className="col-auto ms-3">
                                <div className="btn-group">
                                  <button
                                    className="btn btn-sm"
                                    style={{ 
                                      color: colors.info,
                                      borderColor: colors.info,
                                      backgroundColor: colors.white 
                                    }}
                                    title={t('editAnswer', { ns: 'labels' })}
                                    onClick={() => {
                                      // Save any pending local changes before entering editor
                                      // The onTextChange should have already been called by Language_Text_Editor
                                      onEdit?.(index);
                                    }}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    style={{ 
                                      color: colors.danger,
                                      borderColor: colors.danger,
                                      backgroundColor: colors.white 
                                    }}
                                    title={t('deleteAnswer', { ns: 'labels' })}
                                    onClick={() => onDelete(index)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export { Answers_Builder };